import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  MapPin,
  Target,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { HOME_COORD } from "../../constants/config";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const MAX_ACCURACY_M = 50;

const STATUS_META = {
  valid: { label: "Kunjungan Valid", color: "#22C55E", short: "V" },
  visited: { label: "Kunjungan Valid", color: "#22C55E", short: "V" },
  prospect: { label: "Prospek", color: "#2563EB", short: "P" },
  closing: { label: "Closing", color: "#D97706", short: "C" },
  expired: { label: "Perlu Revisit", color: "#E2231A", short: "!" },
  out_of_area: { label: "Out of Coverage Area", color: "#991B1B", short: "!" },
  blank: { label: "Belum Dikunjungi", color: "#E2231A", short: "!" },
};

function coverageIcon(status) {
  const meta = STATUS_META[status] || STATUS_META.visited;
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      width: 28px;height: 28px;border-radius: 50%;
      background:${meta.color};border: 3px solid #fff;
      box-shadow: 0 4px 10px rgba(0,0,0,0.25);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-weight:800;font-size:11px;
    ">${meta.short}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function FitPoints({ points, currentPos }) {
  const map = useMap();

  useEffect(() => {
    const coords = points.map((p) => [p.latitude, p.longitude]);
    if (currentPos) coords.push([currentPos.lat, currentPos.lng]);
    if (!coords.length) return;
    if (coords.length === 1) {
      map.setView(coords[0], 16);
      return;
    }
    map.fitBounds(coords, { padding: [36, 36], maxZoom: 16 });
  }, [currentPos, map, points]);

  return null;
}

function InvalidateMapSize({ trigger }) {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 260);
    return () => clearTimeout(timer);
  }, [map, trigger]);

  return null;
}

function readPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Browser tidak mendukung GPS."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: options.timeout || 12_000,
      maximumAge: 0,
    });
  });
}

function waitForGpsLock(onUpdate) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Browser tidak mendukung GPS."));
      return;
    }

    let best = null;
    let watchId = null;
    let timer = null;

    const finish = (value, error) => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (timer !== null) clearTimeout(timer);
      if (error) reject(error);
      else resolve(value);
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) {
          best = pos;
          onUpdate?.(pos);
        }
        if (pos.coords.accuracy <= MAX_ACCURACY_M) finish(pos);
      },
      (err) => finish(null, err),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 }
    );

    timer = setTimeout(() => {
      if (best) finish(best);
      else finish(null, new Error("GPS tidak tersedia. Aktifkan lokasi dan coba lagi."));
    }, 20_000);
  });
}

function dataUrlToBlob(dataUrl) {
  const [header, body] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

function formatTime(value) {
  return new Date(value).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

function cameraErrorMessage(err) {
  if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
    return "Izin kamera ditolak. Aktifkan permission kamera di pengaturan browser.";
  }
  if (err?.name === "NotFoundError") {
    return "Kamera tidak ditemukan di perangkat ini.";
  }
  if (err?.name === "OverconstrainedError") {
    return "Kamera belakang tidak tersedia. Mencoba kamera lain...";
  }
  if (err?.name === "NotReadableError") {
    return "Kamera sedang dipakai aplikasi lain. Tutup aplikasi lain dan coba lagi.";
  }
  if (err?.name === "AbortError") {
    return "Akses kamera dibatalkan. Coba lagi.";
  }
  if (!window.isSecureContext) {
    return "Kamera live memerlukan HTTPS. Gunakan tombol kamera HP di bawah sebagai alternatif.";
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return "Browser tidak mendukung kamera live. Gunakan Chrome Android terbaru.";
  }
  return err?.message || "Kamera tidak dapat dibuka. Gunakan tombol kamera HP di bawah.";
}

export default function LiveTracking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureInputRef = useRef(null);
  const [checkins, setCheckins] = useState([]);
  const [stats, setStats] = useState({ visited: 0, prospect: 0, closing: 0 });
  const [currentPos, setCurrentPos] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("Membaca GPS...");
  const [gpsLocking, setGpsLocking] = useState(false);
  const [gpsTest, setGpsTest] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [nativeCaptureReady, setNativeCaptureReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prospectTarget, setProspectTarget] = useState(null);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [form, setForm] = useState({
    prospect_name: "",
    prospect_phone: "",
    interested_product: "",
    notes: "",
  });

  const validCurrentPos = currentPos?.accuracy <= MAX_ACCURACY_M ? currentPos : null;
  const mapCheckins = useMemo(
    () => checkins.filter((item) => !item.out_of_coverage_area && item.visual_status !== "out_of_area"),
    [checkins]
  );
  const latestCheckin = mapCheckins[0];
  const mapCenter = validCurrentPos || (latestCheckin ? {
    lat: latestCheckin.latitude,
    lng: latestCheckin.longitude,
  } : HOME_COORD);

  const todayStats = useMemo(
    () => ({
      visited: stats.area_valid ?? stats.visited ?? 0,
      expired: stats.area_expired || 0,
      prospect: stats.prospect || 0,
      closing: stats.closing || 0,
      validDays: stats.coverage_valid_days || 30,
    }),
    [stats]
  );

  const loadCoverage = useCallback(async () => {
    try {
      const [{ data: rows }, { data: statRows }] = await Promise.all([
        api.get("/coverage/checkins", { params: { days: 30 } }),
        api.get("/coverage/stats", { params: { days: 366 } }),
      ]);
      console.log("[HSCIS][coverage] query dashboard sales result", {
        currentUser: user ? { id: user.id, role: user.role, dealer_id: user.dealer_id } : null,
        rows,
        stats: statRows,
      });
      console.log("[HSCIS][coverage] marker data result", rows);
      setCheckins(rows);
      setStats(statRows);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesi belum aktif di URL IP. Login ulang dari HP memakai alamat ini.");
        navigate("/login", { replace: true });
        return;
      }
      toast.error(err?.response?.data?.detail || "Gagal memuat coverage.");
    }
  }, [navigate, user]);

  const updateGpsPosition = (pos) => {
    const accuracy = pos.coords.accuracy;
    const point = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy,
      ts: new Date().toISOString(),
    };
    setCurrentPos((prev) => (!prev || accuracy <= prev.accuracy ? point : prev));
    setGpsStatus(
      accuracy <= MAX_ACCURACY_M
        ? `GPS valid ${Math.round(accuracy)}m`
        : `GPS belum akurat (${Math.round(accuracy)}m)`
    );
    return point;
  };

  const runGpsTest = async () => {
    try {
      const pos = await readPosition({ timeout: 12_000 });
      const point = updateGpsPosition(pos);
      setGpsTest(point);
      toast.success("GPS test berhasil.");
    } catch (err) {
      toast.error(err?.message || "GPS test gagal.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraStream(null);
    setCameraOpen(false);
  };

  // Bind stream to video element — called after DOM is guaranteed rendered
  const bindStreamToVideo = (stream) => {
    const video = videoRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      video.play().catch(() => {});
    }
  };

  // Re-bind whenever cameraStream updates (e.g. after re-render)
  // Retry binding multiple times to handle slow DOM mount on mobile
  useEffect(() => {
    if (!cameraOpen || !cameraStream) return;
    let cancelled = false;
    const attempts = [80, 300, 700, 1500];
    const timers = attempts.map((delay) =>
      setTimeout(() => {
        if (!cancelled) bindStreamToVideo(cameraStream);
      }, delay)
    );
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [cameraOpen, cameraStream]); // eslint-disable-line react-hooks/exhaustive-deps

  const lockGpsBackground = () => {
    setGpsLocking(true);
    waitForGpsLock(updateGpsPosition)
      .then((pos) => updateGpsPosition(pos))
      .catch(() => {})
      .finally(() => setGpsLocking(false));
  };

  const openCamera = async () => {
    // Always open the modal immediately so user sees the UI right away
    setCameraOpen(true);
    setNativeCaptureReady(true);

    // Start GPS lock in background (non-blocking)
    lockGpsBackground();

    // If no secure context or no getUserMedia API, show toast but keep modal open with native fallback
    if (!window.isSecureContext) {
      toast.info("Live preview kamera memerlukan HTTPS. Gunakan tombol \"📷 Buka Kamera HP\" di bawah untuk check-in.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.info("Browser tidak mendukung live preview. Gunakan tombol \"📷 Buka Kamera HP\" di bawah.");
      return;
    }

    try {
      // Try back camera first (environment), then front/any camera
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (firstErr) {
        if (firstErr?.name === "NotAllowedError" || firstErr?.name === "SecurityError") {
          // Permission denied — don't retry, show error
          throw firstErr;
        }
        // Fallback: accept any camera (e.g. front camera on desktop)
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      }
      streamRef.current = stream;
      setCameraStream(stream);
      // Bind immediately if video is already mounted, useEffect will retry after state settles
      bindStreamToVideo(stream);
    } catch (err) {
      // Live preview failed — modal stays open with native fallback visible
      const msg = cameraErrorMessage(err);
      toast.warning(msg);
    }
  };

  const drawWatermark = (ctx, width, height) => {
    const source = currentPos || validCurrentPos;
    const gpsText = source
      ? `${source.lat.toFixed(6)}, ${source.lng.toFixed(6)} | ±${Math.round(source.accuracy)}m`
      : "GPS tidak tersedia";

    const lines = [
      "Honda Babel HSCIS",
      user?.name || "Sales",
      user?.dealer || user?.dealer_id || "Dealer",
      new Date().toLocaleString("id-ID"),
      gpsText,
    ];
    const pad = Math.max(16, Math.round(width * 0.018));
    const boxHeight = Math.max(132, Math.round(height * 0.18));
    ctx.fillStyle = "rgba(0,0,0,0.58)";
    ctx.fillRect(0, height - boxHeight, width, boxHeight);
    ctx.fillStyle = "#fff";
    ctx.font = `bold ${Math.max(22, Math.round(width * 0.024))}px Arial`;
    ctx.fillText(lines[0], pad, height - boxHeight + 36);
    ctx.font = `${Math.max(18, Math.round(width * 0.019))}px Arial`;
    lines.slice(1).forEach((line, index) => {
      ctx.fillText(line, pad, height - boxHeight + 66 + index * 24);
    });
  };

  const createWatermarkedPhoto = () => {
    const video = videoRef.current;
    if (!video) throw new Error("Kamera belum siap.");
    if (video.readyState < 2) throw new Error("Stream kamera belum siap. Tunggu sebentar dan coba lagi.");

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    drawWatermark(ctx, canvas.width, canvas.height);

    return canvas.toDataURL("image/jpeg", 0.88);
  };

  const createWatermarkedImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Foto tidak bisa dibaca."));
      reader.onload = () => {
        const image = new Image();
        image.onerror = () => reject(new Error("Foto tidak valid."));
        image.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = image.naturalWidth || 1280;
          canvas.height = image.naturalHeight || 720;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          drawWatermark(ctx, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.88));
        };
        image.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Relaxed: 200m still acceptable, warn user but don't block
  const MAX_CHECKIN_ACCURACY_M = 200;

  const saveCheckinWithPhoto = async (dataUrl) => {
    if (!currentPos) {
      toast.error("GPS belum tersedia. Aktifkan lokasi dan coba lagi.");
      return;
    }
    if (currentPos.accuracy > MAX_CHECKIN_ACCURACY_M) {
      toast.error(`GPS kurang akurat (${Math.round(currentPos.accuracy)}m). Tunggu sinyal lebih baik atau pindah ke tempat terbuka.`);
      return;
    }
    if (currentPos.accuracy > MAX_ACCURACY_M) {
      // Warn but allow check-in
      toast.warning(`GPS tidak terlalu akurat (${Math.round(currentPos.accuracy)}m), tetapi check-in diproses.`);
    }

    setSaving(true);
    try {
      console.log("[HSCIS][coverage] currentUser", {
        id: user?.id,
        role: user?.role,
        dealer_id: user?.dealer_id,
        name: user?.name,
      });
      console.log("[HSCIS][coverage] GPS latitude longitude accuracy", {
        latitude: currentPos.lat,
        longitude: currentPos.lng,
        accuracy: currentPos.accuracy,
      });
      const file = dataUrlToBlob(dataUrl);
      const upload = new FormData();
      upload.append("file", file, `coverage-${Date.now()}.jpg`);

      const { data: evidence } = await api.post("/evidence/upload", upload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const payload = {
        latitude: currentPos.lat,
        longitude: currentPos.lng,
        accuracy: currentPos.accuracy,
        photo_url: evidence.url,
        status: "visited",
      };
      console.log("[HSCIS][coverage] payload check-in", payload);

      const { data } = await api.post("/coverage/checkins", payload);
      console.log("[HSCIS][coverage] response insert database", data);

      setCheckins((rows) => [data, ...rows]);
      setStats((prev) => ({
        ...prev,
        visited: (prev.visited || 0) + 1,
      }));
      setForm({ prospect_name: "", prospect_phone: "", interested_product: "", notes: "" });
      stopCamera();
      setNativeCaptureReady(false);
      setProspectTarget(data);
      window.dispatchEvent(new CustomEvent("hscis:coverage-checkin-saved", { detail: data }));
      await loadCoverage();
      toast.success("Check-in area berhasil disimpan! ✅");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Gagal menyimpan check-in.");
    } finally {
      setSaving(false);
      if (captureInputRef.current) captureInputRef.current.value = "";
    }
  };

  const saveLiveCheckin = async () => {
    try {
      const dataUrl = createWatermarkedPhoto();
      await saveCheckinWithPhoto(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Gagal mengambil foto.");
    }
  };

  const handleCaptureFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await createWatermarkedImage(file);
      await saveCheckinWithPhoto(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Gagal memproses foto kamera.");
    }
  };

  const markClosing = async (checkin) => {
    try {
      const { data } = await api.patch(`/coverage/checkins/${checkin.id}`, { status: "closing" });
      console.log("[HSCIS][coverage] response insert database", data);
      setCheckins((rows) => rows.map((row) => (row.id === data.id ? data : row)));
      setStats((prev) => ({
        ...prev,
        prospect: Math.max((prev.prospect || 0) - (checkin.status === "prospect" ? 1 : 0), 0),
        closing: (prev.closing || 0) + 1,
      }));
      window.dispatchEvent(new CustomEvent("hscis:coverage-checkin-saved", { detail: data }));
      await loadCoverage();
      toast.success("Status area diubah menjadi closing.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah status closing.");
    }
  };

  const saveProspect = async () => {
    if (!prospectTarget) return;
    if (!form.prospect_name.trim()) {
      toast.error("Nama prospek wajib diisi.");
      return;
    }

    try {
      const payload = {
        name: form.prospect_name.trim(),
        phone: form.prospect_phone.trim() || null,
        address: prospectTarget.address || `Coverage GPS · ${Number(prospectTarget.latitude).toFixed(4)}, ${Number(prospectTarget.longitude).toFixed(4)}`,
        coord: { lat: prospectTarget.latitude, lng: prospectTarget.longitude },
        interested_product: form.interested_product.trim() || "UNKNOWN",
        status: "new",
        notes: form.notes.trim() || null,
        photo: prospectTarget.photo_url || null,
        coverage_checkin_id: prospectTarget.id,
      };
      console.log("[HSCIS][coverage] payload prospect", {
        ...payload,
        has_photo: Boolean(payload.photo),
      });
      const { data: prospect } = await api.post("/prospects", payload);
      console.log("[HSCIS][coverage] response prospect", prospect);
      const { data: updatedCheckin } = await api.patch(`/coverage/checkins/${prospectTarget.id}`, {
        status: prospect.status === "closing" ? "closing" : "prospect",
        prospect_name: prospect.name,
        prospect_phone: prospect.phone,
        interested_product: prospect.interested_product,
        notes: prospect.notes,
      });
      console.log("[HSCIS][coverage] response insert database", updatedCheckin);
      setCheckins((rows) => rows.map((row) => (row.id === updatedCheckin.id ? updatedCheckin : row)));
      setStats((prev) => ({
        ...prev,
        visited: Math.max((prev.visited || 0) - 1, 0),
        prospect: (prev.prospect || 0) + 1,
      }));
      setForm({ prospect_name: "", prospect_phone: "", interested_product: "", notes: "" });
      setProspectTarget(null);
      window.dispatchEvent(new CustomEvent("hscis:prospect-saved", { detail: prospect }));
      window.dispatchEvent(new CustomEvent("hscis:coverage-checkin-saved", { detail: updatedCheckin }));
      await loadCoverage();
      toast.success("Prospek berhasil ditambahkan.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambahkan prospek.");
    }
  };

  useEffect(() => {
    loadCoverage();
    let watchId = null;
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        updateGpsPosition,
        () => setGpsStatus("GPS belum aktif"),
        { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 }
      );
    } else {
      setGpsStatus("GPS tidak didukung");
    }
    readPosition({ timeout: 8_000 }).then(updateGpsPosition).catch(() => {});
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      stopCamera();
    };
  }, [loadCoverage]);

  const statusSummary = (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-center">
        <div className="flex items-center justify-center gap-1 text-honda-muted">
          <CheckCircle2 size={11} />
          <p className="text-[10px] uppercase tracking-wider font-semibold">Kunjung</p>
        </div>
        <p className="font-heading font-bold text-base text-honda-ink mt-0.5">{todayStats.visited}</p>
      </div>
      <div className="text-center border-x border-honda-border">
        <div className="flex items-center justify-center gap-1 text-honda-muted">
          <UserRound size={11} />
          <p className="text-[10px] uppercase tracking-wider font-semibold">Prospek</p>
        </div>
        <p className="font-heading font-bold text-base text-honda-ink mt-0.5">{todayStats.prospect}</p>
      </div>
      <div className="text-center">
        <div className="flex items-center justify-center gap-1 text-honda-muted">
          <CircleDollarSign size={11} />
          <p className="text-[10px] uppercase tracking-wider font-semibold">Closing</p>
        </div>
        <p className="font-heading font-bold text-base text-honda-ink mt-0.5">{todayStats.closing}</p>
      </div>
    </div>
  );

  return (
    <div data-testid="live-tracking-page" className="relative h-screen min-h-[640px] overflow-hidden bg-white">
      <input
        ref={captureInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleCaptureFile}
      />

      <div className="absolute top-0 left-0 right-0 z-[500] bg-white/95 backdrop-blur border-b border-honda-border px-4 py-2.5 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="tracking-back-button"
          className="w-9 h-9 rounded-full hover:bg-honda-bg flex items-center justify-center"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-base text-honda-ink leading-tight truncate">
            Coverage Check-In
          </p>
          <p className="text-[11px] text-honda-muted flex items-center gap-1 truncate">
            <span className={`h-1.5 w-1.5 rounded-full ${validCurrentPos ? "bg-green-500" : "bg-yellow-500"}`} />
            {gpsLocking ? "Mengunci GPS akurat..." : gpsStatus}
          </p>
        </div>
        <button
          data-testid="tracking-toggle-play"
          onClick={() => openCamera().catch((err) => toast.error(err.message || "Gagal membuka kamera."))}
          className="w-9 h-9 rounded-full bg-honda-red text-white flex items-center justify-center shadow disabled:opacity-60"
        >
          <Camera size={16} />
        </button>
      </div>

      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={14}
        scrollWheelZoom={true}
        zoomControl={false}
        className="h-screen w-full"
      >
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitPoints points={mapCheckins} currentPos={validCurrentPos} />
        <InvalidateMapSize trigger={`${sheetExpanded}-${legendOpen}-${debugOpen}`} />
        {mapCheckins.map((item) => (
          <Marker
            key={item.id}
            position={[item.latitude, item.longitude]}
            icon={coverageIcon(item.visual_status || item.status)}
          >
            <Popup>
              <div className="min-w-[190px] space-y-1 text-[12px]">
                {item.photo_url && (
                  <a href={item.photo_url} target="_blank" rel="noreferrer">
                    <img
                      src={item.photo_url}
                      alt="Foto evidence check-in"
                      className="mb-2 h-24 w-full rounded-lg object-cover"
                    />
                  </a>
                )}
                <b>{STATUS_META[item.visual_status || item.status]?.label}</b>
                <p>Sales: {item.salesman_name || user?.name || "—"}</p>
                <p>Dealer: {item.dealer_id || user?.dealer || "—"}</p>
                <p>Waktu: {formatTime(item.checkin_time)}</p>
                <p>Accuracy GPS: {Math.round(item.accuracy)}m</p>
                <p className="font-mono">
                  {Number(item.latitude).toFixed(6)}, {Number(item.longitude).toFixed(6)}
                </p>
                <p>Valid sampai {new Date(item.valid_until).toLocaleDateString("id-ID")}</p>
                {item.prospect_name && <p>Prospek: {item.prospect_name}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
        {validCurrentPos && (
          <CircleMarker
            center={[validCurrentPos.lat, validCurrentPos.lng]}
            radius={7}
            pathOptions={{ color: "#E2231A", fillColor: "#E2231A", fillOpacity: 0.85, weight: 2 }}
          />
        )}
      </MapContainer>

      <div className="absolute left-4 top-16 z-[500]">
        <button
          onClick={() => setLegendOpen((value) => !value)}
          className="rounded-full bg-white/95 px-3 py-2 text-[11px] font-bold text-honda-ink shadow"
        >
          Legend
        </button>
        {legendOpen && (
          <div className="mt-2 space-y-1 rounded-lg bg-white/95 p-2 shadow">
            {Object.entries(STATUS_META).filter(([key]) => key !== "visited").map(([key, meta]) => (
              <div key={key} className="flex items-center gap-1 rounded px-1 py-0.5 text-[10px]">
                <MapPin size={10} color={meta.color} />
                <span>{meta.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className={`absolute left-0 right-0 z-[520] rounded-t-2xl bg-white shadow-2xl transition-all duration-200 ${
          sheetExpanded ? "bottom-0 max-h-[40vh]" : "bottom-[76px] max-h-[178px]"
        }`}
      >
        <div className="mx-auto h-1.5 w-12 rounded-full bg-honda-border mt-2" />
        <div className="p-4 overflow-y-auto" style={{ maxHeight: sheetExpanded ? "calc(40vh - 8px)" : "170px" }}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openCamera().catch((err) => toast.error(err.message || "Gagal membuka kamera."))}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-honda-red px-3 py-3 text-sm font-heading font-bold text-white shadow disabled:opacity-60"
            >
              <Camera size={16} />
              {gpsLocking ? "MENGUNCI GPS..." : "CHECK-IN AREA"}
            </button>

            <button
              onClick={() => setSheetExpanded((value) => !value)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-honda-border"
            >
              {sheetExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="text-honda-red" size={18} />
              <p className="font-heading font-bold text-sm text-honda-ink">Coverage Hari Ini</p>
            </div>
            <span className="text-[11px] text-honda-muted">
              Valid {todayStats.validDays} hari - {todayStats.expired} revisit
            </span>
          </div>
          <div className="mt-2">{statusSummary}</div>

          {sheetExpanded && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-honda-muted">Area Terbaru</p>
                <button
                  onClick={() => setDebugOpen((value) => !value)}
                  className="rounded-lg bg-honda-ink px-3 py-1.5 text-[11px] font-semibold text-white"
                >
                  Debug GPS
                </button>
              </div>
              <div className="grid gap-1">
                {checkins.slice(0, 5).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.status === "visited") setProspectTarget(item);
                      if (item.status === "prospect") markClosing(item);
                    }}
                    className="w-full rounded border border-honda-border px-2 py-1.5 text-left text-[11px]"
                  >
                    <span className="font-semibold" style={{ color: STATUS_META[item.visual_status || item.status]?.color }}>
                      {STATUS_META[item.visual_status || item.status]?.label}
                    </span>
                    <span className="float-right text-honda-muted">{formatTime(item.checkin_time)}</span>
                  </button>
                ))}
              </div>
              {debugOpen && (
                <div className="rounded-xl border border-honda-border bg-honda-bg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-honda-muted">Device Info</p>
                    <button
                      onClick={() => runGpsTest().catch(() => {})}
                      className="rounded-lg bg-honda-ink px-3 py-1.5 text-[11px] font-semibold text-white"
                    >
                      TEST GPS
                    </button>
                  </div>
                  <div className="mt-2 space-y-1 text-[11px] text-honda-muted">
                    <p>Online: {online ? "Ya" : "Tidak"}</p>
                    <p>Latitude: {gpsTest?.lat?.toFixed?.(6) ?? validCurrentPos?.lat?.toFixed?.(6) ?? "-"}</p>
                    <p>Longitude: {gpsTest?.lng?.toFixed?.(6) ?? validCurrentPos?.lng?.toFixed?.(6) ?? "-"}</p>
                    <p>
                      GPS Accuracy:{" "}
                      {gpsTest?.accuracy
                        ? `${Math.round(gpsTest.accuracy)}m`
                        : currentPos?.accuracy
                          ? `${Math.round(currentPos.accuracy)}m`
                          : "-"}
                    </p>
                    <p className="break-all">User Agent: {navigator.userAgent}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {cameraOpen && (
        <div className="absolute inset-0 z-[700] bg-black flex flex-col">
          {/* Camera preview - full screen */}
          <div className="relative flex-1 bg-black overflow-hidden">
            {/* Show video if stream active, otherwise show placeholder */}
            {cameraStream ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/70">
                <Camera size={48} className="animate-pulse opacity-60" />
                <p className="text-sm font-semibold text-white/80 text-center px-6">
                  Memuat kamera...{"\n"}Jika tidak muncul, gunakan tombol di bawah
                </p>
              </div>
            )}
            {/* GPS overlay */}
            {gpsLocking && (
              <div className="absolute top-3 left-3 bg-black/60 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-yellow-400 animate-pulse" />
                <span className="text-[11px] text-white font-semibold">Mengunci GPS...</span>
              </div>
            )}
            {currentPos && !gpsLocking && (
              <div className="absolute top-3 left-3 bg-black/60 rounded-full px-3 py-1.5">
                <span className="text-[11px] text-white font-mono">
                  {currentPos.lat.toFixed(5)}, {currentPos.lng.toFixed(5)} · {Math.round(currentPos.accuracy)}m
                </span>
              </div>
            )}
            <button
              onClick={stopCamera}
              className="absolute top-3 right-3 bg-black/60 rounded-full px-3 py-1.5 text-white text-xs font-semibold"
            >
              ✕ Tutup
            </button>
          </div>

          {/* Action bar */}
          <div className="bg-white px-4 py-4 space-y-2.5">
            <p className="text-center text-[11px] text-honda-muted">
              Foto akan otomatis diberi watermark GPS + waktu kunjungan
            </p>
            {/* "Ambil Foto" only available when live stream is active */}
            {cameraStream && (
              <button
                onClick={saveLiveCheckin}
                disabled={saving}
                className="w-full rounded-xl bg-honda-red text-white py-3 font-heading font-bold text-sm disabled:opacity-60"
              >
                {saving ? "Menyimpan..." : "📸 Ambil Foto & Simpan Check-In"}
              </button>
            )}
            {/* Always-visible native camera button — guaranteed to work on all phones */}
            <button
              onClick={() => captureInputRef.current?.click()}
              className="w-full rounded-xl border-2 border-honda-red text-honda-red py-3 font-heading font-bold text-sm active:bg-red-50"
            >
              📷 Buka Kamera HP & Simpan Check-In
            </button>
          </div>
        </div>
      )}

      {prospectTarget && (
        <div className="absolute inset-0 z-[720] bg-black/60 flex items-end">
          <div className="w-full rounded-t-2xl bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-sm text-honda-ink">Tambah Prospek</p>
                <p className="text-[11px] text-honda-muted">Area ini akan berubah menjadi status prospek.</p>
              </div>
              <button onClick={() => setProspectTarget(null)} className="text-xs font-semibold text-honda-muted">
                Lewati
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                value={form.prospect_name}
                onChange={(e) => setForm((v) => ({ ...v, prospect_name: e.target.value }))}
                placeholder="Nama prospek"
                className="rounded-lg border border-honda-border px-3 py-2 text-sm"
              />
              <input
                value={form.prospect_phone}
                onChange={(e) => setForm((v) => ({ ...v, prospect_phone: e.target.value }))}
                placeholder="Nomor HP"
                className="rounded-lg border border-honda-border px-3 py-2 text-sm"
              />
              <input
                value={form.interested_product}
                onChange={(e) => setForm((v) => ({ ...v, interested_product: e.target.value }))}
                placeholder="Minat unit"
                className="rounded-lg border border-honda-border px-3 py-2 text-sm"
              />
              <input
                value={form.notes}
                onChange={(e) => setForm((v) => ({ ...v, notes: e.target.value }))}
                placeholder="Catatan"
                className="rounded-lg border border-honda-border px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={saveProspect}
              className="w-full rounded-xl bg-blue-600 py-3 text-sm font-heading font-bold text-white"
            >
              TAMBAH PROSPEK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
